/* eslint-disable global-require */
import Axios, { AxiosError, AxiosInstance } from 'axios'
import * as Sentry from '@sentry/react'
import logError, { getAxiosRequestContext } from './logError'

/**
 * Assign interceptors for responses into Axios instance
 * @param {AxiosInstance} axios - Axios instance
 * @param {Function} showErrorNotifications - Callback function for handling errors
 */
export default (axios: AxiosInstance, showErrorNotifications: (error: AxiosError | Error | unknown) => void) => {
	const handleResponseError = (error: AxiosError | Error | any) => {
		// Aborted requests are expected (e.g. race of calendar/filter fetches) — do not notify or report.
		if (Axios.isCancel(error)) {
			return Promise.reject(error)
		}

		showErrorNotifications(error)

		// If error is not common Axios error or missing response, capture it to Sentry
		if (!Axios.isAxiosError(error)) {
			Sentry.captureException(error, { extra: { message: 'Interceptor: Non-Axios error' } })
		} else if (!error.response) {
			Sentry.captureException(error, { extra: { message: 'Interceptor: No response received' } })
		} else {
			// We want HTTP request context in the Issue event (not only in Logs).
			// Keep grouping stable by attaching details via scope contexts/extras.
			Sentry.withScope((scope) => {
				const ctx = getAxiosRequestContext(error)
				scope.setContext('axios', {
					method: ctx.method,
					baseURL: ctx.baseURL,
					url: ctx.url,
					fullUrl: ctx.fullUrl,
					status: ctx.status,
					requestHeaders: ctx.requestHeaders,
					responseHeaders: ctx.responseHeaders
				})

				Sentry.captureException(error)
			})

			logError(error, 'Axios: HTTP error')
		}

		return Promise.reject(error)
	}

	axios.interceptors.response.use((response) => response, handleResponseError)
}
